/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import { Toolbar } from '@roc/ui';
import { observer } from 'mobx-react';
import { useStore } from '@roc/client-portal-shared/hooks';

import { useTwilioDevice } from '../../hooks/useTwilioDevice';
import { useLeadSelection } from '../../hooks/useLeadSelection';
import {
  getLeadLabel,
  getLeadPhone,
  normalizeToE164,
  GridRow,
  getLeadRow,
} from '../../utils/phone';

import DialControls from './DialControl';
import LeadList from './LeadList';
import LeadDetailsPanel from './LeadDetailsPanel';
import AutoNextDialModal from './AutoNextDialModal';
import { isProduction } from '@roc/feature-utils';
import { LEAD_OBJECT_TYPE } from 'libs/feature-backoffice-users/src/backOfficeUsers/components/leads/constants/constants';

type DialSessionModalProps = {
  leadIds: Array<string | number>;
  onClose?: () => void;
};

type LeadOverride = {
  name?: string;
  rawPhone?: string;
  e164?: string;
  doNotCall?: boolean;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    peopleLayoutContainer: {
      display: 'flex',
      width: '100%',
      height: '80vh',
      border: '1px solid #ccc',
      overflow: 'hidden',
    },
  })
);

const pickUserWorkPhone = (u?: any): string | undefined => {
  return (
    u?.workphone
  );
};

const DialSessionModal: React.FC<DialSessionModalProps> = observer(
  ({ leadIds = [] }) => {
    const classes = useStyles();
    const { salesforceLeadStore, userStore, contactDetailsStore } = useStore();

    const dashboardType =
      salesforceLeadStore?.leadsDashboardStore?.currentDashboard?.objectType?.toUpperCase?.() ??
      salesforceLeadStore?.leadsDashboardStore?.currentDashboard?.baseObject?.toUpperCase?.() ??
      LEAD_OBJECT_TYPE;

    const listTitle = dashboardType === LEAD_OBJECT_TYPE ? 'Leads' : 'Contacts';

    const identity = userStore?.userInformation?.username?.trim() || '';

    const rawFrom = pickUserWorkPhone(userStore?.userInformation) || '';

  const fromNumber = isProduction ? normalizeToE164(String(rawFrom)) : "Testing";

    const badgeLeadRef = useRef<string | null>(null);

    const {
      deviceReady,
      status,
      isMuted,
      hasActiveCall,
      connect,
      hangup,
      toggleMute,
    } = useTwilioDevice({
      identity,
      from: fromNumber,
      refreshMinutes: 50,
      logLevel: 'error',
    });

    const {
      normalized,
      selectedLeadId,
      setSelectedLeadId,
      selectedLeadIds,
      toggleChecked,
    } = useLeadSelection(leadIds);

    const [leadOverrides, setLeadOverrides] = useState<Record<string, LeadOverride>>({});

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const gridRows: GridRow[] =
      salesforceLeadStore?.leadsDashboardStore?.leadsDashboardGridStore
        ?.gridData?.data?.rows ?? [];

    const formFields =
      salesforceLeadStore?.editLeadDetailsFormStore?.form?.fields;

    const liveFirstName = String(
      formFields?.firstName?.value ??
        formFields?.firstname?.value ??
        formFields?.first_name?.value ??
        ''
    ).trim();

    const liveLastName = String(
      formFields?.lastName?.value ??
        formFields?.lastname?.value ??
        formFields?.last_name?.value ??
        ''
    ).trim();

    const liveCombinedName = `${[liveFirstName, liveLastName]
      .filter(Boolean)
      .join(' ')}`.trim();

    const livePrimaryPhone = String(formFields?.phoneNumber?.value ?? '').trim();
    const liveSecondaryPhone = String(formFields?.secondaryPhone?.value ?? '').trim();

    const liveDoNotCallRaw =
      formFields?.doNotCall?.value ?? formFields?.dnc?.value ?? undefined;

    const liveDoNotCall =
      typeof liveDoNotCallRaw === 'boolean'
        ? liveDoNotCallRaw
        : typeof liveDoNotCallRaw === 'string' && liveDoNotCallRaw !== ''
        ? liveDoNotCallRaw.toLowerCase() === 'true'
        : undefined;

    const leadDetails = salesforceLeadStore?.leadDetails;

    useEffect(() => {
      if (dashboardType !== LEAD_OBJECT_TYPE) return;
      if (!selectedLeadId) return;
      if (!leadDetails) return;

      const key = String(selectedLeadId);
      const rawPhone = livePrimaryPhone || liveSecondaryPhone;
      const nextData: LeadOverride = {
        name: liveCombinedName,
        rawPhone,
        e164: normalizeToE164(rawPhone),
        ...(typeof liveDoNotCall === 'boolean' ? { doNotCall: liveDoNotCall } : {}),
      };

      setLeadOverrides(prev => {
        const prevData = prev[key];
        if (
          prevData &&
          prevData.name === nextData.name &&
          prevData.rawPhone === nextData.rawPhone &&
          prevData.e164 === nextData.e164 &&
          prevData.doNotCall === nextData.doNotCall
        ) {
          return prev;
        }

        if (
          !prevData &&
          !nextData.name &&
          !nextData.rawPhone &&
          !nextData.e164 &&
          nextData.doNotCall === undefined
        ) {
          return prev;
        }

        return {
          ...prev,
          [key]: nextData,
        };
      });
    }, [
      dashboardType,
      selectedLeadId,
      leadDetails,
      liveCombinedName,
      livePrimaryPhone,
      liveSecondaryPhone,
      liveDoNotCall,
    ]);

    const getLeadOverride = useCallback(
      (id?: string | null): LeadOverride | undefined => {
        if (!id) return undefined;
        return leadOverrides[String(id)];
      },
      [leadOverrides]
    );

    const getLeadDoNotCall = useCallback(
      (id?: string | null): boolean => {
        if (!id) return false;
        if (dashboardType === LEAD_OBJECT_TYPE) {
          const override = getLeadOverride(id);
          if (override && 'doNotCall' in override && override.doNotCall !== undefined)
            return override.doNotCall;
          if (id && String(id) === String(selectedLeadId)) {
            if (typeof liveDoNotCall === 'boolean') return liveDoNotCall;
          }
        }
        const row = getLeadRow(gridRows, id);
        return !!row?.doNotCall;
      },
      [gridRows, selectedLeadId, getLeadOverride, liveDoNotCall, dashboardType]
    );

    const getLeadName = useCallback(
      (id?: string | null) => {
        if (!id) return '';
        if (dashboardType === LEAD_OBJECT_TYPE) {
          const override = getLeadOverride(id);
          if (override && override.name !== undefined) {
            const fallback = getLeadLabel(gridRows, id) ?? '';
            return override.name || fallback;
          }
          if (String(id) === String(selectedLeadId) && liveCombinedName)
            return liveCombinedName;
        }
        return getLeadLabel(gridRows, id) ?? '';
      },
      [gridRows, selectedLeadId, getLeadOverride, liveCombinedName, dashboardType]
    );

    const getLeadE164 = useCallback(
      (id?: string | null) => {
        if (!id) return '';
        if (dashboardType === LEAD_OBJECT_TYPE) {
          const override = getLeadOverride(id);
          if (override && 'e164' in override) return override.e164 ?? '';

          if (String(id) === String(selectedLeadId)) {
            const rawPhone = livePrimaryPhone || liveSecondaryPhone;
            const liveE164 = normalizeToE164(String(rawPhone ?? ''));
            if (liveE164) return liveE164;
            if (!rawPhone) return '';
          }
        }
        return normalizeToE164(String(getLeadPhone(gridRows, id) ?? ''));
      },
      [
        gridRows,
        selectedLeadId,
        getLeadOverride,
        livePrimaryPhone,
        liveSecondaryPhone,
        dashboardType,
      ]
    );

    const getLeadDisplayPhone = useCallback(
      (id?: string | null) => {
        if (!id) return '';
        const override = getLeadOverride(id);
        if (override && 'rawPhone' in override) return override.rawPhone ?? '';
        return getLeadPhone(gridRows, id) ?? '';
      },
      [gridRows, getLeadOverride]
    );

    const getContactIdentifier = useCallback(
      (id?: string | null) => {
        if (!id) return null;
        const row = getLeadRow(gridRows, id);
        return row?.contactId ?? row?.companyContactId ?? null;
      },
      [gridRows]
    );

    const getRecordTypeForId = useCallback(
      (id?: string | null) => {
        if (dashboardType === LEAD_OBJECT_TYPE) return LEAD_OBJECT_TYPE;

        if (!id) return dashboardType;
        const row = getLeadRow(gridRows, id);
        return (row?.recordType as string | undefined)?.toUpperCase?.() ?? dashboardType;
      },
      [gridRows, dashboardType]
    );

    const dialCurrent = () => {
      if (!selectedLeadId) return;
      if (getLeadDoNotCall(selectedLeadId)) return;
      const e164 = getLeadE164(selectedLeadId);
      if (e164) {
        const contactId = getContactIdentifier(selectedLeadId);
        connect(e164, {
          leadId: selectedLeadId,
          userId: userStore?.userInformation?.userId,
          ...(contactId ? { contactId } : {}),
        });
      }
    };

    const findNextDialableId = useCallback(
      (fromId?: string | null): string | null => {
        if (!normalized?.length) return null;
        const startIdx = fromId
          ? normalized.findIndex(x => String(x) === String(fromId))
          : -1;

        for (let i = startIdx + 1; i < normalized.length; i++) {
          const cand = String(normalized[i]);
          if (!getLeadDoNotCall(cand)) {
            const phone = getLeadE164(cand);
            if (phone) return cand;
          }
        }
        return null;
      },
      [normalized, getLeadDoNotCall, getLeadE164]
    );

    const dialNext = () => {
      const candidate = nextDialCandidate;
      if (!candidate) return;
      if (hasActiveCall) {
        try {
          hangup();
        } catch (e) {
          console.warn("Error hanging up previous call:", e);
        }
    }
      setSelectedLeadId(candidate);
      const e164 = getLeadE164(candidate);
      if (e164) {
        const contactId = getContactIdentifier(candidate);
        connect(e164, {
          leadId: candidate,
          userId: userStore?.userInformation?.userId,
          ...(contactId ? { contactId } : {}),
        });
      }
    };

    const currentLeadName = getLeadName(selectedLeadId);
    const currentLeadPhone = getLeadE164(selectedLeadId);
    const currentLeadIsDoNotCall = selectedLeadId
      ? getLeadDoNotCall(selectedLeadId)
      : false;

    const canDialCurrent =
      !!selectedLeadId && !!currentLeadPhone && !currentLeadIsDoNotCall;

    const currentIndex = normalized.findIndex(
      x => String(x) === String(selectedLeadId)
    );

    const nextDialCandidate = useMemo(() => {
      if (!selectedLeadId) return null;
      return findNextDialableId(selectedLeadId);
    }, [selectedLeadId, findNextDialableId]);

    const canDialNext = deviceReady && !!nextDialCandidate;
    const prevStatusRef = useRef(status);
    const [autoModalOpen, setAutoModalOpen] = useState(false);
    const [nextCandidateId, setNextCandidateId] = useState<string | null>(null);

    const computeNextCandidate = useCallback((): string | null => {
      return nextDialCandidate;
    }, [nextDialCandidate]);

    const refreshActivitiesFor = useCallback(
      (id?: string | null) => {
        if (!id) return;
        const bu = salesforceLeadStore?.gridBusinessUnit;
        const leadDetailsStore = salesforceLeadStore?.leadsStore?.leadDetailsStore;
        const contactLeadDetailsStore = contactDetailsStore?.leadDetailsStore;
        if (!bu) return;

        const recordType = getRecordTypeForId(id);
        const targetStore =
          recordType === LEAD_OBJECT_TYPE
            ? leadDetailsStore
            : contactLeadDetailsStore;

        if (!targetStore) return;

        try {
          targetStore.type = recordType;
          targetStore.objectId = id;
          targetStore.fetchLeadActivities(bu, String(id));
        } catch (e) {
          console.warn('Failed to refresh lead activities', e);
        }
      },
      [salesforceLeadStore, contactDetailsStore, getRecordTypeForId]
    );

    useEffect(() => {
      const prev = prevStatusRef.current;
      const ended = status === 'ended' || status === 'failed';

      if ((prev === 'connected' || prev === 'calling') && ended) {
        const leadToRefresh = badgeLeadRef.current ?? selectedLeadId ?? null;
        window.setTimeout(() => refreshActivitiesFor(leadToRefresh), 1500);
        const candidate = computeNextCandidate();
        if (candidate && deviceReady) {
          setNextCandidateId(candidate);
          setAutoModalOpen(true);
        }
      }
      prevStatusRef.current = status;
    }, [status, deviceReady, computeNextCandidate, selectedLeadId, refreshActivitiesFor]);

    const handleAutoTimeout = useCallback(() => {
      setAutoModalOpen(false);
      if (!nextCandidateId) return;

      const e164 = getLeadE164(nextCandidateId);
      if (!e164) return;

      setSelectedLeadId(nextCandidateId);
      const contactId = getContactIdentifier(nextCandidateId);
      connect(e164, {
        leadId: nextCandidateId,
        userId: userStore?.userInformation?.userId,
        ...(contactId ? { contactId } : {}),
      });
      setNextCandidateId(null);
    }, [
      nextCandidateId,
      getLeadE164,
      connect,
      setSelectedLeadId,
      getContactIdentifier,
    ]);

    const handleAutoPause = useCallback(() => {
      setAutoModalOpen(false);
      setNextCandidateId(null);
    }, []);

    const nextLeadName = getLeadName(nextCandidateId);
    const nextLeadPhone = getLeadDisplayPhone(nextCandidateId);

    useEffect(() => {
      if (status === 'calling' || status === 'connected') {
        badgeLeadRef.current = selectedLeadId ?? null;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const handleSelectLead = useCallback(
      (id: string) => {
        badgeLeadRef.current = null;
        setSelectedLeadId(id);
        setAutoModalOpen(false);
        setNextCandidateId(null);
      },
      [setSelectedLeadId]
    );

    return (
      <>
        <Toolbar toolbarContainerStyle={{ padding: '10px' }}>
          <DialControls
            isMuted={isMuted}
            hasActiveCall={hasActiveCall}
            deviceReady={deviceReady}
            canDialCurrent={canDialCurrent}
            canDialNext={canDialNext}
            onToggleMute={toggleMute}
            onHang={hangup}
            onDialCurrent={dialCurrent}
            onDialNext={dialNext}
            currentLeadName={currentLeadName}
            currentLeadPhone={currentLeadPhone}
          />
        </Toolbar>

        <Box className={classes.peopleLayoutContainer}>
          <LeadList
            leadIds={normalized}
            isSelected={id => id === selectedLeadId}
            isChecked={id => selectedLeadIds.has(id)}
            onSelect={handleSelectLead}
            onToggle={(id, checked) => toggleChecked(id, checked)}
            getLabel={id => getLeadName(id)}
            getDoNotCall={id => getLeadDoNotCall(id)}
            title={listTitle}
            activeLeadId={badgeLeadRef.current ?? undefined}
            activeStatus={status}
          />

          <LeadDetailsPanel
            salesforceLeadStore={salesforceLeadStore}
            selectedLeadId={selectedLeadId}
          />
        </Box>

        <AutoNextDialModal
          open={autoModalOpen}
          leadName={nextLeadName ?? ''}
          leadPhone={nextLeadPhone ?? ''}
          seconds={5}
          onTimeout={handleAutoTimeout}
          onPause={handleAutoPause}
        />
      </>
    );
  }
);

export default DialSessionModal;
